/**
 * "History" (1.Y deliverable 2): every value of a dated field, newest first, read-only (editing a
 * past value is 3.H). A popover of its own markup on the kit's classes, as the When pill's (the
 * kit's Popover fails axe while open, DECISIONS 88 item 2): Esc closes it alone, focus returns to
 * its button.
 */
import { Icon, useLayer, useReturnFocus } from '@codex/ui';
import { useEffect, useId, useRef, useState } from 'react';
import type { HistoryRow } from './dated-model.js';
import { HISTORY, historyTitle, NO_VALUES } from './words.js';

export function HistoryPopover({ label, rows }: { label: string; rows: readonly HistoryRow[] }) {
  const [open, setOpen] = useState(false);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const headingId = useId();
  useLayer(open, () => setOpen(false), 'popover');
  useReturnFocus(open, button);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (panel.current?.contains(target) || button.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);
  useEffect(() => {
    if (open) panel.current?.focus();
  }, [open]);
  return (
    <span className="cx-temporal__history">
      <button
        ref={button}
        type="button"
        className="cx-button cx-button--secondary cx-button--sm"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen(!open)}
      >
        <Icon name="clock" size={14} /> {HISTORY}
      </button>
      {open && (
        <div
          ref={panel}
          id={panelId}
          role="dialog"
          aria-labelledby={headingId}
          tabIndex={-1}
          className="cx-popover cx-temporal__popover"
          data-testid="temporal.history"
        >
          <h2 id={headingId} className="cx-popover__title">
            {historyTitle(label)}
          </h2>
          {rows.length === 0 ? (
            <p className="cx-temporal__hint">{NO_VALUES}</p>
          ) : (
            <ol className="cx-temporal__rows">
              {rows.map((row) => (
                <li key={row.id} className="cx-temporal__row">
                  <span className="cx-temporal__value">{row.value}</span>
                  <span className="cx-temporal__hint">{row.text}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
    </span>
  );
}
